/**
 * NEXUS UI - StatusIndicator
 * Pulsing status dots with concentric rings
 */

import React from 'react';
import type { BaseProps, StatusType, NexusSize } from './types';

export interface StatusIndicatorProps extends BaseProps {
  status: StatusType;
  label?: string;
  size?: NexusSize;
  showRings?: boolean;
  animated?: boolean;
}

const statusColors: Record<StatusType, { color: string; glow: string }> = {
  active: { color: '#00ff88', glow: 'rgba(0, 255, 136, 0.6)' },
  warning: { color: '#ffaa00', glow: 'rgba(255, 170, 0, 0.6)' },
  critical: { color: '#ff3366', glow: 'rgba(255, 51, 102, 0.6)' },
  processing: { color: '#00d4ff', glow: 'rgba(0, 212, 255, 0.6)' },
  inactive: { color: '#484f58', glow: 'transparent' },
};

const sizeConfig: Record<NexusSize, { dot: number; ring1: number; ring2: number }> = {
  sm: { dot: 6, ring1: 10, ring2: 14 },
  md: { dot: 10, ring1: 16, ring2: 22 },
  lg: { dot: 14, ring1: 22, ring2: 30 },
  xl: { dot: 18, ring1: 28, ring2: 38 },
};

const statusLabels: Record<StatusType, string> = {
  active: 'Active',
  warning: 'Warning',
  critical: 'Critical',
  processing: 'Processing',
  inactive: 'Inactive',
};

export const StatusIndicator: React.FC<StatusIndicatorProps> = ({
  status,
  label,
  size = 'md',
  showRings = true,
  animated = true,
  className = '',
  style,
}) => {
  const colors = statusColors[status];
  const config = sizeConfig[size];
  const displayLabel = label ?? statusLabels[status];

  const getPulseAnimation = () => {
    if (!animated) return 'none';
    // Only critical status pulses by default to draw attention
    // Other statuses stay static unless explicitly animated
    switch (status) {
      case 'critical':
        return 'nx-pulse 0.8s ease-in-out infinite';
      case 'processing':
        return 'none'; // Processing uses ripple on rings instead
      default:
        return 'none';
    }
  };

  return (
    <div
      className={`nx-status nx-status--${status} ${className}`}
      style={{
        '--status-color': colors.color,
        '--status-glow': colors.glow,
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        ...style,
      } as React.CSSProperties}
    >
      <div
        style={{
          position: 'relative',
          width: showRings ? config.ring2 : config.dot,
          height: showRings ? config.ring2 : config.dot,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* Outer ring */}
        {showRings && (
          <div
            style={{
              position: 'absolute',
              width: config.ring2,
              height: config.ring2,
              borderRadius: '50%',
              border: `1px solid ${colors.color}`,
              opacity: 0.2,
              /* Removed constant ripple - static rings indicate state without motion */
            }}
          />
        )}

        {/* Inner ring */}
        {showRings && (
          <div
            style={{
              position: 'absolute',
              width: config.ring1,
              height: config.ring1,
              borderRadius: '50%',
              border: `1px solid ${colors.color}`,
              opacity: 0.4,
              /* Removed constant ripple - static rings indicate state without motion */
            }}
          />
        )}

        {/* Main dot */}
        <div
          className="nx-status__dot"
          style={{
            width: config.dot,
            height: config.dot,
            borderRadius: '50%',
            background: colors.color,
            boxShadow: `0 0 10px ${colors.glow}`,
            animation: getPulseAnimation(),
          }}
        />
      </div>

      {displayLabel && (
        <span
          className="nx-status__label"
          style={{
            fontFamily: 'var(--nx-font-interface)',
            fontSize: size === 'sm' ? '10px' : size === 'xl' ? '14px' : '12px',
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            color: colors.color,
            fontWeight: 500,
          }}
        >
          {displayLabel}
        </span>
      )}
    </div>
  );
};

export default StatusIndicator;
